import {
  DatePicker,
  Portal,
  type DatePickerValueChangeDetails,
} from "@chakra-ui/react";
import { HStack, Select, Text } from "components/ui";
import {
  type CalendarDate,
  getLocalTimeZone,
  parseDate,
  today,
} from "@internationalized/date";
import { MdCalendarMonth } from "react-icons/md";
import { useEffect, useState } from "react";

/**
 * Shared dashboard date-range control: a quick-preset selector (7 / 14 / 30 days) plus a
 * theme-aware calendar (Chakra `DatePicker` in range mode) for a custom From–To window.
 * Presentational only — the owning page holds the state (see `useDashboardRange`) and passes
 * translated labels in, so this component stays string-free and reusable across dashboard pages.
 */
export interface DateRangePickerProps {
  /** Current preset key: "7" | "14" | "30" | "custom". */
  preset: string;
  /** Inclusive window start, yyyy-mm-dd. */
  from: string;
  /** Inclusive window end, yyyy-mm-dd. */
  to: string;
  /** Fired when a numeric preset is chosen. */
  onPreset: (days: number) => void;
  /** Fired when a from/to range is picked on the calendar (switches the control to "custom"). */
  onCustom: (from: string, to: string) => void;
  labels: {
    last7: string;
    last14: string;
    last30: string;
    custom: string;
    from: string;
    to: string;
  };
}

/** Parses a yyyy-mm-dd string to a CalendarDate, or null if blank/invalid. */
function toCalendarDate(value: string) {
  if (!value) return null;
  try {
    return parseDate(value);
  } catch {
    return null;
  }
}

/** Compact "Jun 26" style label for a yyyy-mm-dd string (keeps the trigger on one line). */
function shortLabel(value: string): string {
  if (!value) return "";
  const d = new Date(`${value}T00:00:00`);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** A month/year grid cell as returned by the DatePicker api helpers. */
interface GridCell {
  label: string;
  value: number;
}

// Table with no cell spacing so the selected range renders as one solid band.
const gaplessTable = {
  borderCollapse: "separate" as const,
  borderSpacing: 0,
  width: "auto" as const,
};

// Day-cell states, keyed off Ark's data-attributes.
const dayCellCss = {
  "&[data-in-range]": { background: "rgba(59,130,246,0.18)", borderRadius: 0 },
  "&[data-range-start]": {
    borderTopLeftRadius: "6px",
    borderBottomLeftRadius: "6px",
  },
  "&[data-range-end]": {
    borderTopRightRadius: "6px",
    borderBottomRightRadius: "6px",
  },
  "&[data-selected]": {
    background: "var(--chakra-colors-blue-solid)",
    color: "white",
    borderRadius: "6px",
  },
  "&[data-today]": {
    outline: "1px solid var(--border-subtle)",
    outlineOffset: "-1px",
  },
  "&[data-outside-range]": { opacity: 0.35 },
  "&[data-disabled]": { opacity: 0.3, cursor: "not-allowed" },
};

// Month/year cell states (no range band — a single selection).
const gridCellCss = {
  "&[data-selected]": {
    background: "var(--chakra-colors-blue-solid)",
    color: "white",
  },
  "&[data-disabled]": { opacity: 0.3, cursor: "not-allowed" },
};

const navHoverProps = {
  px: 2,
  borderRadius: "md",
  cursor: "pointer",
  _hover: { bg: "rgba(127,127,127,0.12)" },
};

/** Shared prev / title (opens month→year views) / next header used by all three views. */
const RangeNavHeader = () => (
  <DatePicker.ViewControl
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    gap={2}
    mb={2}
  >
    <DatePicker.PrevTrigger {...navHoverProps}>‹</DatePicker.PrevTrigger>
    <DatePicker.ViewTrigger
      fontWeight="semibold"
      fontSize="sm"
      {...navHoverProps}
    >
      <DatePicker.RangeText />
    </DatePicker.ViewTrigger>
    <DatePicker.NextTrigger {...navHoverProps}>›</DatePicker.NextTrigger>
  </DatePicker.ViewControl>
);

export const DateRangePicker = ({
  preset,
  from,
  to,
  onPreset,
  onCustom,
  labels,
}: DateRangePickerProps) => {
  // Local selection state so an in-progress range (one date picked, waiting for the second)
  // isn't discarded. The parent (a complete from/to) is only updated once two dates are chosen.
  const [selected, setSelected] = useState<CalendarDate[]>(() => {
    const s = toCalendarDate(from);
    const e = toCalendarDate(to);
    return s && e ? [s, e] : [];
  });

  // Re-sync when the parent range changes externally (e.g. a preset was picked).
  useEffect(() => {
    const s = toCalendarDate(from);
    const e = toCalendarDate(to);
    setSelected(s && e ? [s, e] : []);
  }, [from, to]);

  const handleValueChange = (details: DatePickerValueChangeDetails) => {
    const picked = details.value as CalendarDate[];
    setSelected(picked);
    if (picked.length >= 2) {
      onCustom(picked[0].toString(), picked[picked.length - 1].toString());
    }
  };

  const triggerLabel = `${shortLabel(from)} – ${shortLabel(to)}`;

  return (
    <HStack gap={2} wrap="nowrap" flexShrink={0}>
      <Select
        value={[preset]}
        onValueChange={(e) => {
          const v = e.value[0];
          if (v !== "custom") onPreset(parseInt(v, 10));
        }}
        w="140px"
        items={[
          { value: "7", label: labels.last7 },
          { value: "14", label: labels.last14 },
          { value: "30", label: labels.last30 },
          { value: "custom", label: labels.custom },
        ]}
      />

      <DatePicker.Root
        selectionMode="range"
        value={selected}
        onValueChange={handleValueChange}
        max={today(getLocalTimeZone())}
        positioning={{ placement: "bottom-end" }}
        // Override the recipe defaults (root is `width: full; flex-direction: column`),
        // which would otherwise stretch and stack, overlapping the neighbouring controls.
        display="inline-flex"
        flexDirection="row"
        alignItems="center"
        width="auto"
        flexShrink={0}
      >
        {/* Control is the element the popup anchors to — keep it, but size it to content
            (recipe default is width:full) so it doesn't stretch/overlap the neighbours. */}
        <DatePicker.Control
          display="inline-flex"
          width="auto"
          position="relative"
        >
          <DatePicker.Trigger
            display="inline-flex"
            alignItems="center"
            gap={2}
            px={3}
            h="40px"
            width="auto"
            minW="0"
            fontSize="sm"
            borderWidth="1px"
            borderColor="var(--border-subtle)"
            borderRadius="md"
            cursor="pointer"
            bg="transparent"
            whiteSpace="nowrap"
            _hover={{ bg: "rgba(127,127,127,0.08)" }}
          >
            <MdCalendarMonth />
            <Text as="span" fontSize="sm" style={{ whiteSpace: "nowrap" }}>
              {triggerLabel}
            </Text>
          </DatePicker.Trigger>
        </DatePicker.Control>

        <Portal>
          <DatePicker.Positioner zIndex="popover">
            <DatePicker.Content
              bg="var(--chakra-colors-bg-panel)"
              borderWidth="1px"
              borderColor="var(--border-subtle)"
              borderRadius="lg"
              boxShadow="lg"
              p={3}
            >
              <DatePicker.View view="day">
                <DatePicker.Context>
                  {(api: DatePicker.UseDatePickerContext) => (
                    <>
                      <RangeNavHeader />
                      <DatePicker.Table style={gaplessTable}>
                        <DatePicker.TableHead>
                          <DatePicker.TableRow>
                            {api.weekDays.map(
                              (weekDay: { short: string }, i: number) => (
                                <DatePicker.TableHeader
                                  key={i}
                                  w="40px"
                                  px={0}
                                  py={1}
                                  fontSize="xs"
                                  color="var(--text-secondary-color)"
                                  fontWeight="medium"
                                  textAlign="center"
                                >
                                  {weekDay.short}
                                </DatePicker.TableHeader>
                              ),
                            )}
                          </DatePicker.TableRow>
                        </DatePicker.TableHead>
                        <DatePicker.TableBody>
                          {api.weeks.map((week: CalendarDate[], i: number) => (
                            <DatePicker.TableRow key={i}>
                              {week.map((day: CalendarDate, j: number) => (
                                <DatePicker.TableCell key={j} value={day} p={0}>
                                  <DatePicker.TableCellTrigger
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    w="40px"
                                    h="36px"
                                    fontSize="sm"
                                    borderRadius="md"
                                    cursor="pointer"
                                    userSelect="none"
                                    _hover={{ bg: "rgba(59,130,246,0.18)" }}
                                    css={dayCellCss}
                                  >
                                    {day.day}
                                  </DatePicker.TableCellTrigger>
                                </DatePicker.TableCell>
                              ))}
                            </DatePicker.TableRow>
                          ))}
                        </DatePicker.TableBody>
                      </DatePicker.Table>
                    </>
                  )}
                </DatePicker.Context>
              </DatePicker.View>

              <DatePicker.View view="month">
                <DatePicker.Context>
                  {(api: DatePicker.UseDatePickerContext) => (
                    <>
                      <RangeNavHeader />
                      <DatePicker.Table style={gaplessTable}>
                        <DatePicker.TableBody>
                          {api
                            .getMonthsGrid({ columns: 4, format: "short" })
                            .map((row: GridCell[], i: number) => (
                              <DatePicker.TableRow key={i}>
                                {row.map((cell: GridCell, j: number) => (
                                  <DatePicker.TableCell
                                    key={j}
                                    value={cell.value}
                                    p={1}
                                  >
                                    <DatePicker.TableCellTrigger
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      w="60px"
                                      h="40px"
                                      fontSize="sm"
                                      borderRadius="md"
                                      cursor="pointer"
                                      _hover={{ bg: "rgba(59,130,246,0.18)" }}
                                      css={gridCellCss}
                                    >
                                      {cell.label}
                                    </DatePicker.TableCellTrigger>
                                  </DatePicker.TableCell>
                                ))}
                              </DatePicker.TableRow>
                            ))}
                        </DatePicker.TableBody>
                      </DatePicker.Table>
                    </>
                  )}
                </DatePicker.Context>
              </DatePicker.View>

              <DatePicker.View view="year">
                <DatePicker.Context>
                  {(api: DatePicker.UseDatePickerContext) => (
                    <>
                      <RangeNavHeader />
                      <DatePicker.Table style={gaplessTable}>
                        <DatePicker.TableBody>
                          {api
                            .getYearsGrid({ columns: 4 })
                            .map((row: GridCell[], i: number) => (
                              <DatePicker.TableRow key={i}>
                                {row.map((cell: GridCell, j: number) => (
                                  <DatePicker.TableCell
                                    key={j}
                                    value={cell.value}
                                    p={1}
                                  >
                                    <DatePicker.TableCellTrigger
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      w="60px"
                                      h="40px"
                                      fontSize="sm"
                                      borderRadius="md"
                                      cursor="pointer"
                                      _hover={{ bg: "rgba(59,130,246,0.18)" }}
                                      css={gridCellCss}
                                    >
                                      {cell.label}
                                    </DatePicker.TableCellTrigger>
                                  </DatePicker.TableCell>
                                ))}
                              </DatePicker.TableRow>
                            ))}
                        </DatePicker.TableBody>
                      </DatePicker.Table>
                    </>
                  )}
                </DatePicker.Context>
              </DatePicker.View>
            </DatePicker.Content>
          </DatePicker.Positioner>
        </Portal>
      </DatePicker.Root>
    </HStack>
  );
};

export default DateRangePicker;
